import { FileText, ImageIcon, LinkIcon, Tag } from "lucide-react"
import type { FormEvent } from "react"
import { useEffect, useState } from "react"

import { RichTextEditor } from "@/components/rich-text-editor"
import { Button } from "@/components/ui/button"
import { CardContent, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"

export interface InterviewFormData {
  title: string
  interviewee_name?: string
  status: "published" | "draft" | "archived"
  content: string
  cover?: File | null
  cover_url?: string
  remove_cover?: boolean
}

export interface InterviewFormProps {
  title: string
  data: InterviewFormData
  errors: Record<string, string>
  processing?: boolean
  onChange: (partial: Partial<InterviewFormData>) => void
  onSubmit: (e: FormEvent) => void
  onCancel: () => void
  submitLabel?: string
  currentCover?: string | null
}

export function InterviewForm({
  title,
  data,
  errors,
  processing = false,
  onChange,
  onSubmit,
  onCancel,
  submitLabel = "Guardar",
  currentCover = null,
}: InterviewFormProps) {
  const err = errors || {}
  const [imageMode, setImageMode] = useState<"file" | "url">("file")
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)

  useEffect(() => {
    // File preview
    if (data.cover instanceof File) {
      const url = URL.createObjectURL(data.cover)
      setPreviewUrl(url)
      return () => URL.revokeObjectURL(url)
    }

    // URL preview
    const url = (data.cover_url ?? "").trim()
    const isImage = /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(url)
    if (isImage) {
      setPreviewUrl(url)
      return
    }

    // Current cover from server (unless flagged for removal)
    if (currentCover && !data.remove_cover) {
      setPreviewUrl(currentCover)
      return
    }

    setPreviewUrl(null)
  }, [data.cover, data.cover_url, data.remove_cover, currentCover])

  return (
    <div className="w-full max-w-4xl">
      <CardTitle className="text-foreground flex items-center gap-3 px-4 text-2xl font-bold">
        <FileText className="text-primary h-8 w-8" />
        {title}
      </CardTitle>
      <CardContent className="px-4">
        {Object.keys(err).length > 0 && (
          <div className="alert-error mb-6">
            <div className="mb-2 flex items-center gap-2">
              <div className="bg-status-error h-2 w-2 rounded-full"></div>
              <p className="text-destructive font-semibold">
                Por favor corrige los siguientes errores:
              </p>
            </div>
            <ul className="text-destructive list-disc space-y-1 pl-6 text-sm">
              {Object.entries(err).map(([k, v]) => (
                <li key={k}>{v}</li>
              ))}
            </ul>
          </div>
        )}

        <form onSubmit={onSubmit} className="space-y-4">
          {/* Detalles */}
          <div className="space-y-4">
            <div>
              <Label className="text-muted-foreground mb-2 block text-sm font-medium">
                Título
              </Label>
              <Input
                value={data.title}
                onChange={(e) => onChange({ title: e.target.value })}
                required
                className="border-border focus:border-primary h-12 border-2 text-base transition-colors duration-200"
                placeholder="Ej: Historias de migración"
              />
              {err.title && (
                <p className="text-destructive mt-1 text-sm">{err.title}</p>
              )}
            </div>

            <div>
              <Label className="text-muted-foreground mb-2 block text-sm font-medium">
                Entrevistado/a
              </Label>
              <Input
                value={data.interviewee_name ?? ""}
                onChange={(e) => onChange({ interviewee_name: e.target.value })}
                className="border-border focus:border-primary h-12 border-2 text-base transition-colors duration-200"
                placeholder="Nombre del entrevistado/a"
              />
              {err.interviewee_name && (
                <p className="text-destructive mt-1 text-sm">
                  {err.interviewee_name}
                </p>
              )}
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <Label className="text-muted-foreground mb-2 block text-sm font-medium">
                  Estado
                </Label>
                <Select
                  value={data.status}
                  onValueChange={(v) =>
                    onChange({ status: v as InterviewFormData["status"] })
                  }
                >
                  <SelectTrigger className="border-border focus:border-primary h-12 border-2 px-3 py-5">
                    <SelectValue placeholder="Estado" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="published">Publicada</SelectItem>
                    <SelectItem value="draft">Borrador</SelectItem>
                    <SelectItem value="archived">Archivada</SelectItem>
                  </SelectContent>
                </Select>
                {err.status && (
                  <p className="text-destructive mt-1 text-sm">{err.status}</p>
                )}
              </div>
            </div>
          </div>

          {/* Portada */}
          <div className="space-y-4">
            <div className="mb-4 flex items-center gap-3">
              <div
                className="bg-primary h-6 w-1 rounded"
                aria-hidden="true"
              ></div>
              <h3 className="text-foreground flex items-center gap-2 text-lg font-semibold">
                <ImageIcon className="text-primary h-5 w-5" />
                Portada
              </h3>
            </div>

            {previewUrl && (
              <div className="bg-muted/50 border-muted flex items-center gap-4 rounded-lg border-2 border-dashed p-4">
                <div className="border-border relative h-24 w-40 overflow-hidden rounded-lg border-2">
                  <img
                    src={previewUrl}
                    alt="Vista previa de portada"
                    className="h-full w-full object-cover"
                  />
                </div>
                {currentCover && !data.remove_cover && (
                  <Button
                    type="button"
                    variant="destructive"
                    size="sm"
                    onClick={() =>
                      onChange({
                        remove_cover: true,
                        cover: null,
                        cover_url: "",
                      })
                    }
                    className="h-10"
                  >
                    <Tag className="h-4 w-4" />
                    Eliminar
                  </Button>
                )}
              </div>
            )}

            <div className="flex min-w-0 flex-wrap items-center gap-2">
              <Button
                type="button"
                variant={imageMode === "file" ? "default" : "outline"}
                size="sm"
                onClick={() => setImageMode("file")}
                className="h-8 shrink-0 px-4"
              >
                <ImageIcon className="mr-2 h-4 w-4" />
                Subir archivo
              </Button>
              <Button
                type="button"
                variant={imageMode === "url" ? "default" : "outline"}
                size="sm"
                onClick={() => setImageMode("url")}
                className="h-8 shrink-0 px-4"
              >
                <LinkIcon className="mr-2 h-4 w-4" />
                Usar URL
              </Button>
            </div>

            {imageMode === "file" && (
              <div className="w-full max-w-full min-w-0">
                <Input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0] ?? null
                    onChange({ cover: file, remove_cover: false })
                  }}
                  className="border-muted file:bg-primary/10 file:text-primary hover:file:bg-primary/20 h-12 w-full border-2 border-dashed file:mr-4 file:truncate file:rounded-lg file:border-0 file:px-4 file:py-2 file:text-sm file:font-medium"
                />
              </div>
            )}

            {imageMode === "url" && (
              <div className="w-full max-w-full min-w-0">
                <Input
                  type="url"
                  placeholder="https://ejemplo.com/imagen.jpg"
                  value={data.cover_url ?? ""}
                  onChange={(e) =>
                    onChange({ cover_url: e.target.value, remove_cover: false })
                  }
                  className="border-border focus:border-primary h-12 w-full border-2 text-base transition-colors duration-200"
                />
                {(() => {
                  const url = (data.cover_url ?? "").trim()
                  const isYouTube = /(^|\.)youtube\.com|youtu\.be/i.test(url)
                  const isImageExt = /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(
                    url,
                  )
                  if (url.length === 0) {
                    return (
                      <div className="text-muted-foreground mt-1 text-xs">
                        Pegá una URL directa de imagen. Ej:
                        https://sitio.com/foto.jpg.
                      </div>
                    )
                  }
                  if (isYouTube) {
                    return (
                      <div className="text-destructive mt-1 text-xs">
                        Este campo solo acepta URLs directas de imagen (jpg,
                        png, webp, …). Para videos de YouTube, insertá el link
                        dentro del contenido.
                      </div>
                    )
                  }
                  if (!isImageExt) {
                    return (
                      <div className="mt-1 text-xs text-amber-600">
                        Esta URL no parece ser una imagen directa. Debe terminar
                        en .jpg, .png, .webp, etc.
                      </div>
                    )
                  }
                  return null
                })()}
              </div>
            )}
          </div>

          {/* Contenido */}
          <div className="space-y-4">
            <div className="mb-1 flex items-center gap-3">
              <div
                className="bg-accent h-6 w-1 rounded"
                aria-hidden="true"
              ></div>
              <h3 className="text-foreground flex items-center gap-2 text-lg font-semibold">
                <FileText className="text-primary h-5 w-5" />
                Contenido
              </h3>
            </div>
            <RichTextEditor
              value={data.content}
              onChange={(html) => onChange({ content: html })}
              placeholder="Escribe el contenido de la entrevista..."
              className="text-base"
              minHeight="220px"
            />
            {err.content && (
              <p className="text-destructive mt-1 text-sm">{err.content}</p>
            )}
          </div>

          <div className="border-border flex justify-end gap-4 border-t pt-6">
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              className="h-9 bg-transparent px-4 text-sm font-medium"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={processing}
              className="h-9 px-4 text-sm font-medium"
            >
              {submitLabel}
            </Button>
          </div>
        </form>
      </CardContent>
    </div>
  )
}
